import { useEffect } from 'react';
import {
  DEFAULT_LOCALE,
  LOCALES,
  HREFLANG,
  OG_LOCALES,
  localePath,
} from '../i18n/config';
import { useI18n } from '../i18n/LanguageContext';
import { slugFor } from '../content/slugs';

export const SITE_URL = 'https://manifestifyer.com';
export const SITE_NAME = 'Manifestify';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Replaces the hreflang set with one link per locale plus x-default.
 * These are what tell Google that /blog and /tr/blog are the same page in
 * different languages rather than duplicates competing with each other.
 */
function setAlternates(hrefFor) {
  document.head.querySelectorAll('link[data-seo-alt]').forEach((el) => el.remove());

  const add = (hreflang, href) => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    el.setAttribute('href', href);
    el.setAttribute('data-seo-alt', 'true');
    document.head.appendChild(el);
  };

  LOCALES.forEach((loc) => add(HREFLANG[loc], hrefFor(loc)));
  add('x-default', hrefFor(DEFAULT_LOCALE));
}

/**
 * Per-route SEO tags. Updates the tags that ship in the static index.html in
 * place, so each route gets a unique title, description, canonical URL, social
 * tags, hreflang alternates and JSON-LD once React renders.
 *
 * Pass `routePath` for a fixed route ('/', '/blog'), or `postId` for an
 * article — the slug differs per locale, so alternates are resolved from the
 * post's id rather than from the URL you happen to be on.
 */
function Seo({
  title,
  description,
  routePath = '/',
  postId = null,
  type = 'website',
  image = DEFAULT_IMAGE,
  jsonLd = null,
  noindex = false,
}) {
  const { locale } = useI18n();
  const jsonLdString = jsonLd ? JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : null;

  useEffect(() => {
    const pathFor = (loc) =>
      postId ? localePath(loc, `/blog/${slugFor(postId, loc)}`) : localePath(loc, routePath);
    const hrefFor = (loc) => `${SITE_URL}${pathFor(loc)}`;
    const url = hrefFor(locale);

    document.documentElement.setAttribute('lang', locale);
    document.title = title;
    setMeta('name', 'description', description);
    setCanonical(url);

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:locale', OG_LOCALES[locale]);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);

    // A noindex page (404) must not advertise itself as a translation target.
    if (noindex) {
      document.head.querySelectorAll('link[data-seo-alt]').forEach((el) => el.remove());
    } else {
      setAlternates(hrefFor);
    }

    let robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex');
    } else if (robots) {
      robots.remove();
    }

    document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    if (jsonLdString) {
      JSON.parse(jsonLdString).forEach((item) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo-jsonld', 'true');
        s.textContent = JSON.stringify(item);
        document.head.appendChild(s);
      });
    }
  }, [title, description, routePath, postId, type, image, jsonLdString, noindex, locale]);

  return null;
}

export default Seo;
